'use client'

import { useRouter } from 'next/navigation'
import React, { useCallback, useState, useRef } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import ReCAPTCHA from 'react-google-recaptcha'

import type { FormWithContentBlock as FormWithContentBlockProps } from '@/payload-types'

import { SectionWrapper } from '@/components/SectionWrapper'
import SectionHeader from '@/components/ui/SectionHeader'
import { CMSLink } from '@/components/Link'
import MotionFadeIn from '@/components/ui/MotionFadeIn'
import { Button } from '@/components/ui/button'
import { normalizeSectionSettings } from '@/utilities/sectionSettings'
import { getClientSideURL } from '@/utilities/getURL'
import { track, deriveLeadType } from '@/utilities/analytics'
import { contactFormFields } from '../ContactForm/FormFields'
import { fields as standardFields } from '@/blocks/Form/fields'
import RichText from '@/components/RichText'

export const FormWithContentBlockClient: React.FC<FormWithContentBlockProps> = ({
  infoTagText,
  title,
  subtitle,
  links,
  form: formFromProps,
  settings,
}) => {
  const normalizedSettings = {
    ...normalizeSectionSettings(settings),
    isDarkMode: settings?.isDarkMode ?? true,
  }
  const isDarkMode = normalizedSettings.isDarkMode

  // Extract form properties with proper type checking
  const formID = typeof formFromProps === 'string' ? formFromProps : formFromProps?.id
  const confirmationMessage =
    typeof formFromProps === 'object' ? formFromProps.confirmationMessage : undefined
  const confirmationType =
    typeof formFromProps === 'object' ? formFromProps.confirmationType : undefined
  const redirect = typeof formFromProps === 'object' ? formFromProps.redirect : undefined
  const submitButtonLabel =
    typeof formFromProps === 'object' ? formFromProps.submitButtonLabel : undefined
  const [requireRecaptcha, setRequireRecaptcha] = React.useState<boolean | undefined>(
    typeof formFromProps === 'object' ? (formFromProps as any).requireRecaptcha : undefined,
  )

  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY

  // Fetch requireRecaptcha if not included in form data
  // The form builder plugin may not include all fields in the serialized form data
  React.useEffect(() => {
    const formId = typeof formFromProps === 'string' ? formFromProps : formFromProps?.id

    // If requireRecaptcha is not in the form props, fetch it from PayloadCMS API
    if (formId && requireRecaptcha === undefined && typeof formFromProps === 'object') {
      fetch(`${getClientSideURL()}/api/forms/${formId}?depth=0`)
        .then((res) => {
          if (!res.ok) {
            throw new Error(`Failed to fetch form: ${res.status}`)
          }
          return res.json()
        })
        .then((formData: any) => {
          if (formData?.requireRecaptcha !== undefined) {
            setRequireRecaptcha(Boolean(formData.requireRecaptcha))
          }
        })
        .catch((err) => {
          console.warn('Failed to fetch form requireRecaptcha:', err)
        })
    }
  }, [formFromProps])

  const formMethods = useForm({
    defaultValues:
      typeof formFromProps === 'object' ? formFromProps.fields || undefined : undefined,
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>()
  const [error, setError] = useState<{ message: string; status?: string } | undefined>()
  const router = useRouter()

  const formName = typeof formFromProps === 'object' ? formFromProps?.title || '' : ''
  const startedRef = useRef(false)
  const handleFormFocus = () => {
    if (startedRef.current) return
    startedRef.current = true
    track('form_started', {
      form_id: formID,
      form_name: formName,
      form_block: 'formWithContent',
      lead_type: deriveLeadType(formName),
    })
  }

  const onSubmit = useCallback(
    (data: any) => {
      const submitForm = async () => {
        setError(undefined)

        // Get reCAPTCHA token if required
        const captchaValue = recaptchaRef.current ? recaptchaRef.current.getValue() : undefined

        // Validate reCAPTCHA if required
        if (requireRecaptcha && !captchaValue) {
          setError({
            message: 'Bitte bestätigen Sie, dass Sie kein Roboter sind.',
          })
          return
        }

        // Set loading state immediately for instant feedback
        setIsLoading(true)

        const formData = new FormData()
        formData.append('form', formID || '')

        // Add reCAPTCHA token to form data if provided
        if (captchaValue) {
          formData.append('recaptcha', captchaValue)
        }

        Object.entries(data || {}).forEach(([name, value]) => {
          if (value instanceof FileList) {
            Array.from(value).forEach((file) => formData.append(name, file))
            return
          }

          if (Array.isArray(value) && value.length && value[0] instanceof File) {
            value.forEach((file: File) => formData.append(name, file))
            return
          }

          const normalizedValue =
            typeof value === 'string' || value instanceof Blob
              ? value
              : typeof value === 'number' || typeof value === 'boolean'
                ? String(value)
                : (value ?? '')

          formData.append(name, normalizedValue as any)
        })

        try {
          const req = await fetch(`${getClientSideURL()}/api/submit-form`, {
            body: formData,
            method: 'POST',
          })

          const res = await req.json()

          if (req.status >= 400) {
            setIsLoading(false)

            setError({
              message: res.error || res.errors?.[0]?.message || 'Internal Server Error',
              status: res.status || req.status,
            })

            // Reset reCAPTCHA on error
            recaptchaRef.current?.reset()
            return
          }

          setIsLoading(false)
          setHasSubmitted(true)

          track('generate_lead', {
            form_id: formID,
            form_name: formName,
            form_block: 'formWithContent',
            lead_type: deriveLeadType(formName),
          })

          // Reset reCAPTCHA after successful submission
          recaptchaRef.current?.reset()

          if (confirmationType === 'redirect' && redirect) {
            const { url } = redirect

            const redirectUrl = url

            if (redirectUrl) router.push(redirectUrl)
          }
        } catch (err) {
          console.warn(err)
          setIsLoading(false)
          setError({
            message: 'Something went wrong.',
          })
          // Reset reCAPTCHA on error
          recaptchaRef.current?.reset()
        }
      }

      void submitForm()
    },
    [router, formID, redirect, confirmationType, requireRecaptcha],
  )

  return (
    <SectionWrapper settings={normalizedSettings}>
      <div data-theme={isDarkMode ? 'dark' : undefined}>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-16 items-start">
          {/* Left: header + links (3/5) */}
          <div className="md:col-span-3">
            <MotionFadeIn className="flex flex-col gap-6 md:gap-8">
              <SectionHeader
                infoTagText={infoTagText || ''}
                title={title}
                subtitle={subtitle || undefined}
                titleClassName="text-[48px] md:text-[48px] tracking-[-0.96px] text-text-white"
                subtitleClassName="text-[20px] text-text-secondary"
                className="mb-0"
              />
            </MotionFadeIn>

            {links && links.length > 0 && (
              <MotionFadeIn
                initialY={20}
                duration={0.7}
                delay={0.15}
                viewportMargin="-50px"
                className="mt-6 flex flex-col sm:flex-row items-start gap-6 md:gap-12"
              >
                {links.map((linkItem, index) => {
                  if (!linkItem.link) return null

                  return (
                    <CMSLink
                      key={index}
                      {...linkItem.link}
                      newTab={linkItem.link.openInNewTab}
                      appearance="external"
                      size="lg"
                      externalColor="light"
                    />
                  )
                })}
              </MotionFadeIn>
            )}
          </div>

          {/* Form (right, 2/5) */}
          <MotionFadeIn className="bg-[var(--card)] p-6 md:p-8 shadow-[0_1px_0_0_rgba(0,0,0,0.04)] space-y-5 md:col-span-2">
            <FormProvider {...formMethods}>
              {!isLoading &&
                hasSubmitted &&
                confirmationType === 'message' &&
                confirmationMessage && <RichText data={confirmationMessage} enableProse={false} />}
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
                  <strong>{error.status || 'Fehler'}:</strong> {error.message || ''}
                </div>
              )}
              {!hasSubmitted && (
                <form id={formID} onSubmit={handleSubmit(onSubmit)} onFocus={handleFormFocus}>
                  <div className="space-y-5">
                    {typeof formFromProps === 'object' &&
                      formFromProps?.fields &&
                      formFromProps.fields?.map((field, index) => {
                        // Use custom contact form fields for text, email, textarea
                        // Fall back to standard fields for other types
                        const ContactField: React.FC<any> =
                          contactFormFields[field.blockType as keyof typeof contactFormFields]
                        const StandardField: React.FC<any> =
                          standardFields[field.blockType as keyof typeof standardFields]

                        const Field = ContactField || StandardField

                        if (Field) {
                          return (
                            <div key={index}>
                              <Field
                                form={formFromProps}
                                {...field}
                                {...formMethods}
                                control={control}
                                errors={errors}
                                register={register}
                              />
                            </div>
                          )
                        }
                        return null
                      })}
                  </div>

                  {requireRecaptcha && siteKey && (
                    <div className="mt-5 px-4 pt-2 pb-4">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={siteKey}
                        className="overflow-hidden rounded-sm"
                      />
                    </div>
                  )}
                  {requireRecaptcha && !siteKey && (
                    <div className="mt-5 px-4 pt-2 pb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
                      ⚠️ reCAPTCHA ist aktiviert, aber NEXT_PUBLIC_RECAPTCHA_SITE_KEY ist nicht
                      gesetzt. Bitte setzen Sie die Umgebungsvariable.
                    </div>
                  )}

                  <div className="mt-5 space-y-2">
                    <Button
                      form={formID}
                      type="submit"
                      variant="primary"
                      color="brand"
                      loading={isLoading}
                      disabled={isLoading}
                    >
                      {submitButtonLabel || 'Senden'}
                    </Button>
                    {isLoading && (
                      <p className="text-sm text-text-secondary text-center">
                        Bitte warten Sie, während das Formular gesendet wird...
                      </p>
                    )}
                  </div>
                </form>
              )}
            </FormProvider>
          </MotionFadeIn>
        </div>
      </div>
    </SectionWrapper>
  )
}
